import * as CheckboxPrimitive from 'radix-ui/checkbox';
import { type Ref, useId } from 'react';
import { cx } from '../cx';
import { MOTION } from '../motion';

/**
 * APPLE'S CHECKBOX, WHICH IS A ROUNDED SQUARE (E4, finding C5).
 *
 * The box was a circle here until 2026-09-12 (`rounded-control`, 10px on a 20px box). Every
 * number below is the macOS 27 kit's own `Toggles - Checkboxes/<Light|Dark>/Content Area`
 * geometry, read out of the .sketch file by tool and recorded in
 * `crm-design/research/CRM-WAVE4-CHECKBOX-KIT-LOOKUP-2026-09-12.md`; the colours live in
 * `tokens.css` in the `E4 checkbox` block with one provenance line each.
 *
 * THE BOX IS A FILL AND NOTHING ELSE. No border in any state, no shadow: all 300 masters on
 * that page were scanned for both and the page has zero borders and no Content Area shadow.
 * There is no hover state either - the kit's state axis runs 1 - Idle, 3 - Clicked,
 * 4 - Disabled with 2 missing - so the pointer's only state here is `:active`.
 *
 * TWO APPEARANCES. `square` is that macOS control and is what every existing caller gets.
 * `circle` is the iOS kit's `Lists/<Light|Dark>/Rows/Editing/Editing <Selected|Unselected>`,
 * the only leading selection control iOS ships, for the phone's task rows and multi-select
 * lists; it is built here so the phone has the part, and no caller uses it yet.
 *
 * THE TWO GLYPHS ARE THE KIT'S OWN CONTROL GLYPHS, not `ui/ObjectGlyph` symbols and not
 * drawings of ours: the checkmark is the `Check` shapePath's twelve curve points converted
 * to a normalised cubic path, and the mixed mark is the `Dash` rectangle. Both are placed by
 * the master's own frame inside the master's own box, so the SVG user units below ARE the
 * kit's numbers.
 *
 * The label is associated with `htmlFor`, not by wrapping: Radix renders a
 * <button role="checkbox">, and a wrapping label would not forward clicks to it.
 */

/** The kit's `Check` outline, normalised to a unit square (report section 1d). */
const CHECK_PATH =
  'M 0.3936 1.0000 C 0.4332 1.0000 0.4632 0.9844 0.4843 0.9516 C 0.4843 0.9516 0.9782 0.1643 0.9782 0.1643 C 0.9932 0.1401 1.0000 0.1181 1.0000 0.0967 C 1.0000 0.0405 0.9591 0.0000 0.9038 0.0000 C 0.8663 0.0000 0.8431 0.0142 0.8199 0.0519 C 0.8199 0.0519 0.3915 0.7589 0.3915 0.7589 C 0.3915 0.7589 0.1726 0.4737 0.1726 0.4737 C 0.1514 0.4467 0.1282 0.4346 0.0962 0.4346 C 0.0402 0.4346 0.0000 0.4758 0.0000 0.5327 C 0.0000 0.5576 0.0075 0.5797 0.0280 0.6046 C 0.0280 0.6046 0.3049 0.9559 0.3049 0.9559 C 0.3288 0.9858 0.3568 1.0000 0.3936 1.0000 Z';

/**
 * One entry per drawn box: the SVG viewBox is the master's own box in the master's own
 * units, and the check's transform is the master's own glyph frame inside it.
 *
 * `3 Rg` and `4 Lg` come from the report's table 1a; the iOS frame is the knocked-out
 * shapePath of `Lists/Dark/Rows/Editing/Editing Selected` (report section 2).
 */
const GEOMETRY = {
  regular: {
    box: 'size-(--checkbox-size) rounded-(--checkbox-radius)',
    viewBox: '0 0 16 16',
    check: 'translate(3.347 3.537) scale(9.306 8.925)',
  },
  large: {
    box: 'size-(--checkbox-size-large) rounded-(--checkbox-radius-large)',
    viewBox: '0 0 18 18',
    check: 'translate(3.141 3.351) scale(11.718 11.299)',
  },
  circle: {
    box: 'size-(--checkbox-circle-size) rounded-(--checkbox-circle-radius)',
    viewBox: '0 0 22 22',
    check: 'translate(5.331 5.829) scale(11.017 10.696)',
  },
} as const;

export type CheckboxAppearance = 'square' | 'circle';
export type CheckboxSize = 'regular' | 'large';

export interface CheckboxProps {
  readonly checked: boolean | 'indeterminate';
  readonly onCheckedChange: (checked: boolean) => void;
  /** Visible text. Omit only when `ariaLabel` is supplied instead. */
  readonly label?: string | undefined;
  readonly ariaLabel?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly id?: string | undefined;
  /**
   * ROVING TABINDEX (`ui-foundation.md` D10). A list of forty checkboxes is forty tab
   * stops unless the list owns the sequence: `OptionList` gives one row `0` and the rest
   * `-1`, and moves focus with the arrow keys itself.
   */
  readonly tabIndex?: number | undefined;
  /** The underlying control, for the list that has to MOVE focus onto a row. */
  readonly ref?: Ref<HTMLButtonElement> | undefined;
  readonly className?: string | undefined;
  /**
   * `square` is Apple's macOS checkbox and the default every existing caller gets;
   * `circle` is the iOS row's leading selection control, for the phone.
   */
  readonly appearance?: CheckboxAppearance | undefined;
  /** The kit's `3 Rg` (16px) and `4 Lg` (18px). The circle has one size only. */
  readonly size?: CheckboxSize | undefined;
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  ariaLabel,
  disabled = false,
  id,
  tabIndex,
  ref,
  className,
  appearance = 'square',
  size = 'regular',
}: CheckboxProps) {
  const fallbackId = useId();
  const controlId = id ?? fallbackId;
  const circle = appearance === 'circle';
  const geometry = GEOMETRY[circle ? 'circle' : size];
  /** Checked and mixed paint the same box; only the glyph inside it differs. */
  const on = checked !== false;

  return (
    <div
      className={cx(
        'inline-flex min-h-(--touch-target-min) items-center gap-(--checkbox-label-gap)',
        'text-2xs select-none',
        disabled ? 'text-(--color-checkbox-label-disabled)' : 'text-ink',
        className,
      )}
    >
      <CheckboxPrimitive.Root
        id={controlId}
        checked={checked}
        disabled={disabled}
        {...(tabIndex === undefined ? {} : { tabIndex })}
        {...(ref === undefined ? {} : { ref })}
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        className={cx(
          // The box is DRAWN at the kit's 16px and the row is 40px tall, so the ROW clears
          // criterion 1's touch floor and the control does not have to be chunky to do it.
          // `hit-expand` grows the pressable box to the floor and changes no pixel
          // (index.css); the kit master's own height is 24 at 3 Rg, and the difference
          // between 24 and 40 is this product's touch rule, not Apple's number.
          'hit-expand touch-manipulation relative isolate flex shrink-0 items-center justify-center',
          // ARROW CURSOR, disabled included: a checkbox is a control, and `not-allowed` is a
          // web convention rather than anything Apple draws.
          'cursor-default',
          MOTION.state,
          geometry.box,
          // `group`, because the pressed layer over the accent is a child and `:active` is
          // the parent's. Nothing else in this component reads the group.
          'group',
          circle
            ? on
              ? 'bg-accent'
              : cx(
                  'border-solid [border-color:var(--color-checkbox-circle-ring)]',
                  '[border-width:var(--checkbox-circle-ring-width)]',
                )
            : on
              ? 'bg-accent'
              : disabled
                ? 'bg-(--color-checkbox-off-disabled)'
                : 'bg-(--color-checkbox-off) active:bg-(--color-checkbox-off-pressed)',
          // The kit dims the whole ON box when it is disabled, and leaves the OFF box at its
          // own tertiary fill. The circle has no disabled master at all, so it follows the
          // square's rule in both states (see the states page's OPEN caption).
          disabled && (on || circle) && 'opacity-(--checkbox-disabled-opacity)',
        )}
      >
        {/*
         * THE SECOND FILL OF A CHECKED STATE. The kit stacks a wash over the blue and its
         * own swatch group says how: "(use plus lighter)" in the dark column, which is a
         * real CSS blend mode, and "(use plus darker)" in the light one, which is WebKit's
         * alone - so `--checkbox-on-blend` is `plus-lighter` in the dark and `normal` in
         * the light. `isolate` on the box above is what keeps the blend inside this control.
         */}
        {on && !circle && (
          <span
            aria-hidden="true"
            className={cx(
              'pointer-events-none absolute inset-0 rounded-[inherit]',
              '[mix-blend-mode:var(--checkbox-on-blend)]',
              disabled
                ? 'bg-(--color-checkbox-on-layer-disabled)'
                : cx(
                    'bg-(--color-checkbox-on-layer)',
                    'group-active:bg-(--color-checkbox-on-layer-pressed)',
                  ),
            )}
          />
        )}
        <CheckboxPrimitive.Indicator
          className={cx(
            'pointer-events-none absolute inset-0 text-(--color-checkbox-glyph)',
            disabled && 'opacity-(--checkbox-glyph-disabled-opacity)',
          )}
        >
          {checked === 'indeterminate' ? (
            /*
             * THE MIXED MARK IS THE KIT'S `Dash` RECTANGLE. The square draws its own size's
             * rectangle; the circle draws the 3 Rg one in the 3 Rg box, scaled with the
             * control, because the iOS kit ships no mixed editing circle at all.
             *
             * THE CORNER IS 1 AND THE KIT SAYS 5. A corner radius cannot exceed half the
             * shorter side, so the kit's `fixedRadius` 5 on a 2-unit-high rectangle draws a
             * pill - a uniform radius of 1. Written as 1 rather than 5 because SVG clamps
             * `rx` and `ry` per AXIS, which would turn the same declaration into a 6.5 x 2
             * ellipse instead of the kit's stadium.
             */
            <svg
              className="size-full"
              viewBox={circle || size === 'regular' ? '0 0 16 16' : '0 0 18 18'}
              fill="currentColor"
              aria-hidden="true"
            >
              {circle || size === 'regular' ? (
                <rect x="4.75" y="7" width="6.5" height="2" rx="1" />
              ) : (
                <rect x="5" y="8" width="8" height="2" rx="1" />
              )}
            </svg>
          ) : (
            <svg
              className="size-full"
              viewBox={geometry.viewBox}
              fill="currentColor"
              aria-hidden="true"
            >
              <g transform={geometry.check}>
                <path d={CHECK_PATH} />
              </g>
            </svg>
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label !== undefined && (
        <label htmlFor={controlId} className="cursor-default">
          {label}
        </label>
      )}
    </div>
  );
}
